import { Box, Divider, MenuItem, Stack, Typography } from '@mui/material';
// @mui
import { alpha } from '@mui/material/styles';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
// auth
import { useAuthContext } from 'src/legacy/auth/useAuthContext';
import { IconButtonAnimate } from 'src/legacy/components/animate';
// components
import { CustomAvatar } from 'src/legacy/components/custom-avatar';
import MenuPopover from 'src/legacy/components/menu-popover';
import { useSnackbar } from 'src/legacy/components/snackbar';
import { useLocales } from 'src/legacy/locales';
// routes
import { PATH_AUTH } from 'src/legacy/routes/paths';
import { useActiveCompanyUser } from 'src/organization/hooks/useActiveCompanyUser';
import { useGetCompanyList } from 'src/organization/hooks/useGetCompanyList';
import { organizationStore } from 'src/organization/store';

// ----------------------------------------------------------------------

const OPTIONS = [
  {
    label: 'home',
    linkTo: '/',
  },
  {
    label: 'profile',
    linkTo: '/dashboard/profile',
  },
  {
    label: 'settings',
    linkTo: '/',
  },
];

const AccountPopover = () => {
  const { logout, user } = useAuthContext();
  const { translate } = useLocales();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const { activeCompanyUser } = useActiveCompanyUser();
  const { items: companies } = useGetCompanyList();

  const [openPopover, setOpenPopover] = useState<HTMLElement | null>(null);

  const handleOpenPopover = (event: React.MouseEvent<HTMLElement>) => {
    setOpenPopover(event.currentTarget);
  };

  const handleClosePopover = () => {
    setOpenPopover(null);
  };

  const handleLogout = async () => {
    try {
      logout();
      navigate(PATH_AUTH.login, { replace: true });
      handleClosePopover();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(`${translate('notification.warning.no_logout')}`, {
        variant: 'error',
      });
    }
  };

  const switchCompany = () => {
    organizationStore.getState().unsetActiveCompany();
    handleClosePopover();
  };

  const handleClickItem = (path: string) => {
    handleClosePopover();
    navigate(path);
  };

  const userDisplayName = `${activeCompanyUser?.first_name || ''} ${
    activeCompanyUser?.last_name || ''
  }`;

  return (
    <>
      <IconButtonAnimate
        onClick={handleOpenPopover}
        sx={{
          p: 0,
          ...(openPopover && {
            '&:before': {
              bgcolor: (theme) => alpha(theme.palette.grey[900], 0.8),
              borderRadius: '50%',
              content: "''",
              height: '100%',
              position: 'absolute',
              width: '100%',
              zIndex: 1,
            },
          }),
        }}
      >
        <CustomAvatar
          alt={userDisplayName}
          name={userDisplayName}
          src={user?.photoURL}
        />
      </IconButtonAnimate>

      <MenuPopover
        onClose={handleClosePopover}
        open={openPopover}
        sx={{ p: 0, width: 200 }}
      >
        <Box sx={{ my: 1.5, px: 2.5 }}>
          <Typography noWrap variant="subtitle2">
            {userDisplayName}
          </Typography>

          <Typography noWrap sx={{ color: 'text.secondary' }} variant="body2">
            {user?.email}
          </Typography>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Stack sx={{ p: 1 }}>
          {OPTIONS.map((option) => (
            <MenuItem
              key={option.label}
              onClick={() => handleClickItem(option.linkTo)}
            >
              {`${translate(option.label)}`}
            </MenuItem>
          ))}
          {companies.length > 1 && (
            <MenuItem onClick={switchCompany}>
              {`${translate('SWITCH_COMPANY')}`}
            </MenuItem>
          )}
        </Stack>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <MenuItem onClick={handleLogout} sx={{ m: 1 }}>
          {`${translate('logout')}`}
        </MenuItem>
      </MenuPopover>
    </>
  );
};

export default AccountPopover;
